import {
	useGetAllUsersLazyQuery,
	useUserRemoveLoginMutation,
	type User,
} from "@repo/server-api";
import { useDebounce } from "@repo/shared/common";
import { useNotification } from "@repo/shared/components";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { PaginationModel, UseLoginsListReturn } from "../types/UserTypes";

export function useLoginsList(): UseLoginsListReturn {
	const { t } = useTranslation();
	const { showNotification } = useNotification();

	const [rows, setRows] = useState<User[]>([]);
	const [rowCount, setRowCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);

	const [searchValue, setSearchValue] = useState("");
	const [paginationModel, setPaginationModel] = useState<PaginationModel>({
		page: 0,
		pageSize: 10,
	});

	// Debounce search value
	const debouncedSearchValue = useDebounce(searchValue, 300);

	const [getUsers] = useGetAllUsersLazyQuery({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
			setIsLoading(false);
		},
	});

	const [deleteUserLogin] = useUserRemoveLoginMutation();

	// Fetch data with current filters
	const fetchData = useCallback(async () => {
		setIsLoading(true);

		const response = await getUsers({
			variables: {
				first: paginationModel.pageSize,
				after: (paginationModel.pageSize * paginationModel.page).toString(),
				search: debouncedSearchValue || null,
			},
		});

		if (response.data?.allUsers?.nodes) {
			setRows(response.data.allUsers.nodes as User[]);
			setRowCount(response.data.allUsers.totalCount);
		}

		setIsLoading(false);
	}, [getUsers, paginationModel, debouncedSearchValue]);

	// Delete a user
	const deleteUser = useCallback(
		async (userId: string) => {
			const response = await deleteUserLogin({
				variables: { userId },
			});

			if (response.data?.userRemoveLogin.boolean) {
				showNotification(t("login.deleted-successfully"), "success");
				fetchData();
			} else {
				showNotification(t("login.delete-failed"), "error");
			}
		},
		[deleteUserLogin, fetchData, showNotification, t],
	);

	// Refresh data
	const refresh = useCallback(() => {
		fetchData();
	}, [fetchData]);

	// Fetch data when dependencies change
	useEffect(() => {
		fetchData();
	}, [paginationModel, debouncedSearchValue]);

	return {
		rows,
		rowCount,
		isLoading,

		searchValue,
		setSearchValue,

		paginationModel,
		setPaginationModel,

		deleteUser,
		refresh,
	};
}
